// components/ActionButtons.tsx
import React from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { CustomerDetailFormState } from '../types/customer';
import { components } from '../theme';

interface ActionButtonsProps {
  isNew: boolean;
  loading: boolean;
  formData: CustomerDetailFormState;
  hasMatch: boolean;
  hasNoEmailChecked: boolean;
  hasNoPhoneChecked: boolean;
  allNull: boolean;
  someNull: boolean;
  customerToken?: string | null;
  onDelete: () => void;
  onSubmit: () => void;
  onAssociateToken?: () => void;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  isNew,
  loading,
  formData,
  hasMatch,
  hasNoEmailChecked,
  hasNoPhoneChecked,
  allNull,
  someNull,
  customerToken,
  onDelete,
  onSubmit,
  onAssociateToken,
}) => {
  const showWarning = (someNull ||
    allNull ||
    (((!hasNoEmailChecked &&
      (formData.email === "" || !formData.email)) ||
      (!hasNoPhoneChecked && formData.phones.length === 0)) &&
      !formData.isDeceased));

  return (
    <Box
      sx={{
        mt: 2,
        ml: 2,
        mr: 3.5,
        bgcolor: "transparent",
        color: "transparent",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        zIndex: 1,
      }}
    >
      <Box sx={{ display: "flex", gap: 1 }}>
        {!isNew ? (
          <Button
            variant="outlined"
            color="error"
            onClick={onDelete}
            disabled={true}
            startIcon={<DeleteIcon />}
          >
            Delete Customer
          </Button>
        ) : (
          <Box sx={{ width: 160 }} />
        )}
        {!isNew && customerToken && onAssociateToken && (
          <Button
            variant="outlined"
            color="primary"
            onClick={onAssociateToken}
            disabled={loading}
          >
            Associate
          </Button>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {showWarning && (
          <Tooltip
            title={
              <>
                {allNull && (
                  <>
                    All communication preferences should be gathered for all
                    channels.
                    <br />
                  </>
                )}
                {someNull && (
                  <>
                    Communication preferences should be gathered for all
                    channels.
                    <br />
                  </>
                )}
                {!hasNoEmailChecked &&
                  !formData.isDeceased &&
                  (formData.email === "" || !formData.email) && (
                    <>
                      If the customer has no email, please confirm by checking
                      the "Customer has no email" checkbox.
                      <br />
                    </>
                  )}
                {!hasNoPhoneChecked &&
                  formData.phones.length === 0 &&
                  !formData.isDeceased && (
                    <>
                      If the customer has no phones, please confirm by
                      checking the "Customer has no phone checkbox.
                      <br />
                    </>
                  )}
              </>
            }
            placement="top"
          >
            <span>
              <WarningAmberIcon
                sx={{
                  color: allNull ? "error.main" : "warning.main",
                  fontSize: components.icon.large.width,
                  verticalAlign: "middle",
                  mr: 2,
                }}
              />
            </span>
          </Tooltip>
        )}
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={
            loading ||
            !formData.firstName ||
            !formData.lastName ||
            !formData.dateOfBirth ||
            hasMatch ||
            (!hasNoEmailChecked &&
              (formData.email === "" || !formData.email) &&
              !formData.isDeceased) ||
            (!hasNoPhoneChecked &&
              formData.phones.length === 0 &&
              !formData.isDeceased)
          }
        >
          {isNew ? "Create Customer" : "Save Changes"}
        </Button>
      </Box>
    </Box>
  );
};